import React, { useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";
import { IApiServiceError } from "store";
import type { TopicRow } from "store/types/news";

import type { FeedConfigBuckets } from "../../context/NewsFeedContext";
import { btnGhostSx, btnPrimarySx, btnSx, fieldInputSx, fieldLabelSx, sectionLabelSx } from "./styles";

const topicTypeBorder: Record<string, string> = {
  "Financial topic": "#b0d0f5",
  Person: "#c4bef2",
  Company: "#8dd4cc",
  Keyword: "#dbe8f5"
};

const typeActiveColor: Record<string, string> = {
  "Financial topic": "#0288d1",
  Keyword: "#071f35",
  Person: "#6e56cf",
  Company: "#26a6ae"
};

const typeActiveBorder: Record<string, string> = {
  "Financial topic": "#b0d0f5",
  Keyword: "#dbe8f5",
  Person: "#c4bef2",
  Company: "#8dd4cc"
};

type Props = {
  initialTopics: FeedConfigBuckets<TopicRow>;
  onCreateTopic: (payload: TopicRow) => Promise<TopicRow>;
  onUpdateTopic: (payload: TopicRow) => Promise<TopicRow>;
  onDeleteTopic: (id: number) => Promise<void>;
  onSelectSystemTopic: (id: number) => Promise<void>;
  onUnselectSystemTopic: (id: number) => Promise<void>;
};

export const TopicsPanel: React.FC<Props> = ({
  initialTopics,
  onCreateTopic,
  onUpdateTopic,
  onDeleteTopic,
  onSelectSystemTopic,
  onUnselectSystemTopic
}) => {
  const [topics, setTopics] = useState<FeedConfigBuckets<TopicRow>>(initialTopics);
  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [newTopicName, setNewTopicName] = useState("");
  const [newTopicType, setNewTopicType] = useState<string>("Financial topic");
  const [editTopicName, setEditTopicName] = useState("");
  const [editTopicType, setEditTopicType] = useState<string>("Financial topic");
  const [newTopicApiError, setNewTopicApiError] = useState<string | null>(null);

  const selTopic = useMemo(
    () => (selectedTopicId != null ? (topics.user.find((t) => t.id === selectedTopicId) ?? null) : null),
    [topics.user, selectedTopicId]
  );

  useEffect(() => {
    if (selTopic) {
      setEditTopicName(selTopic.title);
      setEditTopicType(selTopic.type ?? "Financial topic");
    }
  }, [selTopic?.id]);

  const userTopics = topics.user;
  const systemTopics = topics.system;

  const toggleSelectTopic = (id: number) => {
    setComposerOpen(false);
    setNewTopicApiError(null);
    setSelectedTopicId((cur) => (cur === id ? null : id));
  };

  const removeTopic = async (id: number) => {
    const target = topics.user.find((t) => t.id === id);
    if (!target) return;
    if (selectedTopicId === id) setSelectedTopicId(null);
    if (target.user_id == null) {
      await onUnselectSystemTopic(id);
      setTopics((curr) => ({
        system: [...curr.system, target],
        user: curr.user.filter((t) => t.id !== id)
      }));
    } else {
      await onDeleteTopic(id);
      setTopics((curr) => ({
        ...curr,
        user: curr.user.filter((t) => t.id !== id)
      }));
    }
  };

  const addSuggestedTopic = async (id: number) => {
    const target = topics.system.find((t) => t.id === id);
    if (!target) return;
    await onSelectSystemTopic(id);
    setTopics((curr) => ({
      system: curr.system.filter((t) => t.id !== id),
      user: [...curr.user, target]
    }));
  };

  const saveTopicEdit = async () => {
    if (!selTopic) return;
    const title = editTopicName.trim() || selTopic.title;
    const updated = await onUpdateTopic({ ...selTopic, title, type: editTopicType });
    setTopics((curr) => ({
      ...curr,
      user: curr.user.map((topic) => (topic.id === updated.id ? updated : topic))
    }));
    setSelectedTopicId(null);
  };

  const addNewTopic = async () => {
    const title = newTopicName.trim();
    if (!title) return;
    setNewTopicApiError(null);
    try {
      const created = await onCreateTopic({
        id: 0,
        user_id: null,
        title,
        description: "Custom topic",
        type: newTopicType
      });
      setComposerOpen(false);
      setNewTopicName("");
      setTopics((curr) => ({ ...curr, user: [...curr.user, created] }));
    } catch (error) {
      setNewTopicApiError(Object.values((error as IApiServiceError).errors)[0][0] || "Something went wrong");
    }
  };

  const TopicChip: React.FC<{ t: TopicRow }> = ({ t }) => {
    const border = (t.type && topicTypeBorder[t.type]) || "#dbe8f5";
    const selected = selectedTopicId === t.id;
    return (
      <Chip
        label={t.title}
        onClick={() => toggleSelectTopic(t.id)}
        onDelete={(e) => {
          e.stopPropagation();
          removeTopic(t.id);
        }}
        deleteIcon={
          <Typography component="span" sx={{ fontSize: 13, color: "#797979", lineHeight: 1, pr: "2px" }}>
            ×
          </Typography>
        }
        variant="outlined"
        sx={{
          padding: "5px",
          height: 25,
          fontSize: 11,
          fontWeight: 500,
          color: "#5d7782",
          borderColor: border,
          borderWidth: 1,
          bgcolor: "#fff",
          boxShadow: selected ? `inset 0 0 0 1.5px ${"#071f35"}` : "none",
          "& .MuiChip-label": { px: "9px" },
          "& .MuiChip-deleteIcon": { fontSize: 13, color: "#797979", m: 0, "&:hover": { color: "#071f35" } }
        }}
      />
    );
  };

  const TypeBtn: React.FC<{
    label: string;
    type: string;
    activeType: TopicRow["type"];
    onClick: (t: string) => void;
  }> = ({ label, type, activeType, onClick }) => {
    const isActive = activeType === type;
    return (
      <Button
        onClick={() => onClick(type)}
        disableRipple
        sx={{
          height: 25,
          minWidth: 0,
          borderRadius: "100px",
          border: `1px solid ${isActive ? typeActiveBorder[type] : "#dbe8f5"}`,
          bgcolor: "#fff",
          color: isActive ? typeActiveColor[type] : "#5d7782",
          px: "9px",
          fontSize: 11,
          fontWeight: 500,
          textTransform: "none",
          "&:hover": { bgcolor: "#f6f8fc", borderColor: isActive ? typeActiveBorder[type] : "#dbe8f5" }
        }}
      >
        {label}
      </Button>
    );
  };

  return (
    <Stack direction="column" gap="10px">
      <Typography sx={sectionLabelSx}>Active topics</Typography>
      <Stack direction="row" flexWrap="wrap" gap="5px" sx={{ mb: "10px" }}>
        {userTopics.length ? (
          userTopics.map((t) => <TopicChip key={t.id} t={t} />)
        ) : (
          <Typography sx={{ fontSize: 12, color: "#797979" }}>No topics yet</Typography>
        )}
      </Stack>

      {selTopic ? (
        <>
          <Typography sx={{ ...sectionLabelSx, mt: "12px" }}>Edit topic</Typography>
          <Box sx={{ mb: "10px" }}>
            <Typography component="label" sx={fieldLabelSx}>
              Topic name
            </Typography>
            <TextField
              fullWidth
              hiddenLabel
              variant="outlined"
              value={editTopicName}
              onChange={(e) => setEditTopicName(e.target.value)}
              sx={fieldInputSx}
            />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px", mb: "9px" }}>
            <TypeBtn label="Person" type="Person" activeType={editTopicType} onClick={setEditTopicType} />
            <TypeBtn label="Company" type="Company" activeType={editTopicType} onClick={setEditTopicType} />
            <TypeBtn
              label="Financial topic"
              type="Financial topic"
              activeType={editTopicType}
              onClick={setEditTopicType}
            />
            <TypeBtn label="Keyword" type="Keyword" activeType={editTopicType} onClick={setEditTopicType} />
          </Box>
          <Box sx={{ display: "flex", gap: "7px" }}>
            <Button onClick={saveTopicEdit} sx={btnSx}>
              Save
            </Button>
            <Button onClick={() => setSelectedTopicId(null)} sx={btnGhostSx}>
              Cancel
            </Button>
          </Box>
        </>
      ) : composerOpen ? (
        <>
          <Typography sx={{ ...sectionLabelSx, mt: "12px" }}>New topic</Typography>
          <Box sx={{ mb: "10px" }}>
            <Typography component="label" sx={fieldLabelSx}>
              Topic name
            </Typography>
            <TextField
              fullWidth
              hiddenLabel
              variant="outlined"
              placeholder="Person, company, or keyword …"
              value={newTopicName}
              onChange={(e) => {
                setNewTopicApiError(null);
                setNewTopicName(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") addNewTopic();
              }}
              error={Boolean(newTopicApiError)}
              helperText={newTopicApiError ?? undefined}
              FormHelperTextProps={{ sx: { fontSize: 11, mx: 0, mt: "6px" } }}
              sx={fieldInputSx}
            />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px", mb: "9px" }}>
            <TypeBtn label="Person" type="Person" activeType={newTopicType} onClick={setNewTopicType} />
            <TypeBtn label="Company" type="Company" activeType={newTopicType} onClick={setNewTopicType} />
            <TypeBtn
              label="Financial topic"
              type="Financial topic"
              activeType={newTopicType}
              onClick={setNewTopicType}
            />
            <TypeBtn label="Keyword" type="Keyword" activeType={newTopicType} onClick={setNewTopicType} />
          </Box>
          <Box sx={{ display: "flex", gap: "7px" }}>
            <Button onClick={addNewTopic} sx={btnPrimarySx}>
              Add topic
            </Button>
            <Button
              onClick={() => {
                setNewTopicName("");
                setNewTopicType("Financial topic");
                setNewTopicApiError(null);
                setComposerOpen(false);
              }}
              sx={btnGhostSx}
            >
              Cancel
            </Button>
          </Box>
        </>
      ) : (
        <Button
          onClick={() => {
            setNewTopicApiError(null);
            setComposerOpen(true);
            setSelectedTopicId(null);
          }}
          startIcon={<AddIcon sx={{ fontSize: 15 }} />}
          sx={{ ...btnSx, width: "110px", "& .MuiButton-startIcon": { mr: "5px" } }}
        >
          Add topic
        </Button>
      )}

      {systemTopics.length ? (
        <>
          <Typography sx={{ ...sectionLabelSx, mt: "16px" }}>Suggested topics</Typography>
          {systemTopics.map((t, i) => (
            <Box
              key={t.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "8px",
                py: "7px",
                borderBottom: i < systemTopics.length - 1 ? "1px solid rgba(219,232,245,0.6)" : "none"
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#071f35", lineHeight: 1.35 }}>
                  {t.title}
                </Typography>
                <Typography sx={{ fontSize: 10, color: "#797979", mt: "2px", lineHeight: 1.35 }}>
                  {t.description}
                </Typography>
              </Box>
              <Button onClick={() => addSuggestedTopic(t.id)} sx={{ ...btnSx, height: 26, fontSize: 11 }}>
                Add
              </Button>
            </Box>
          ))}
        </>
      ) : null}
    </Stack>
  );
};
